import React from 'react';
import { Box, HStack, useTheme, VStack } from 'native-base';
import { useTranslation } from 'react-i18next';
import { RotateCcw } from 'lucide-react';

import { Button, Modal, Text } from '@pimm/base';

export type ResetConfirmationAction = 'assignments' | 'schedules';

type ResetConfirmationModalProps = {
  action: ResetConfirmationAction | null;
  onConfirm: () => void;
  onClose: () => void;
};

export const ResetConfirmationModal = ({ action, onConfirm, onClose }: ResetConfirmationModalProps) => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  const lastActionRef = React.useRef<ResetConfirmationAction | null>(action);
  if (action !== null) lastActionRef.current = action;
  const displayedAction = action ?? lastActionRef.current;

  return (
    <Modal size="sm" _content={{ rounded: 'xl', w: '420px' }} hideClose isOpen={!!action} onClose={onClose}>
      <VStack space={4} p={2}>
        <Box w={10} h={10} rounded="lg" bg="red.100" alignItems="center" justifyContent="center">
          <RotateCcw size={18} color={colors.error[700]} />
        </Box>

        <VStack space={1}>
          <Text fontWeight={700} size="lg" color={colors.gray[900]}>
            {displayedAction === 'assignments' ? t('cleaning_task:reset_auto_assignments') : t('cleaning_task:reset_schedules')}
          </Text>
          <Text size="md" color={colors.gray[600]} lineHeight="sm">
            {displayedAction === 'assignments'
              ? t('cleaning_task:reset_assignments_message')
              : t('cleaning_task:reset_schedules_and_assignments_message')}{' '}
            {t('cleaning_task:reset_cannot_be_undone')}
          </Text>
        </VStack>

        <HStack space={2} justifyContent="flex-end">
          <Button variant="unstyled" onPress={onClose}>
            {t('cleaning_task:cancel')}
          </Button>
          <Button
            onPress={onConfirm}
            borderWidth={0}
            bg="error.500"
            _hover={{ bg: 'error.600' }}
            _pressed={{ bg: 'error.600' }}
            _text={{ color: 'white', fontWeight: 600 }}
          >
            {displayedAction === 'assignments'
              ? t('cleaning_task:reset_assignments_confirm')
              : t('cleaning_task:reset_schedules_and_assignments')}
          </Button>
        </HStack>
      </VStack>
    </Modal>
  );
};
